import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import MapView from "@/components/MapView";
import { Headphones, Sparkles, Send, Clock, MapPin, ChevronRight, Loader2 } from "lucide-react";

export default function Dispatch() {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState(null);

  const load = async () => {
    const [b, d] = await Promise.all([
      api.get("/admin/bookings?status=pending"),
      api.get("/drivers/online"),
    ]);
    setBookings(b.data || []);
    setDrivers(d.data || []);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  async function assign(driverId) {
    if (!selected) return;
    try {
      await api.post("/admin/assign", { booking_id: selected.id, driver_id: driverId });
      toast.success("Driver assigned");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function autoGroup() {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/auto-group");
      setGroups(data);
      toast.success(`${data.total} groups proposed`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-[calc(100vh-7rem)] grid grid-cols-12 gap-4">
      {/* Queue */}
      <div className="col-span-12 lg:col-span-4 bg-white rounded-2xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Headphones size={18} className="text-primary" />
            <p className="font-display font-bold text-lg">Pending queue</p>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{bookings.length} bookings awaiting dispatch</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {bookings.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">Queue clear</p>
          ) : bookings.map((b) => (
            <button
              key={b.id}
              data-testid={`queue-${b.id}`}
              onClick={() => setSelected(b)}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                selected?.id === b.id ? "border-primary bg-primary-50/50" : "border-slate-100 hover:border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-slate-400">{b.ref}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold uppercase tracking-wider">
                  {b.type}
                </span>
              </div>
              <p className="font-semibold text-slate-900 text-sm mt-1 truncate">{b.user?.name}</p>
              <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-1">
                <MapPin size={11} />{b.pickup_address?.split(",")[0]} → {b.dropoff_address?.split(",")[0]}
              </p>
              <p className="text-[10px] text-slate-400 mt-1 font-mono">
                <Clock size={10} className="inline" /> {new Date(b.created_at).toLocaleTimeString()}
              </p>
            </button>
          ))}
        </div>
        <div className="p-3 border-t border-slate-100">
          <button
            data-testid="auto-group-btn"
            onClick={autoGroup}
            disabled={loading || bookings.length < 2}
            className="w-full h-11 rounded-xl bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            Auto-group similar routes
          </button>
        </div>
      </div>

      {/* Map */}
      <div className="col-span-12 lg:col-span-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="h-full">
          <MapView
            vehicles={drivers.filter(d => d.current_lat).map(d => ({ id: d.id, current_lat: d.current_lat, current_lng: d.current_lng }))}
            pickup={selected ? { lat: selected.pickup_lat, lng: selected.pickup_lng } : null}
            dropoff={selected ? { lat: selected.dropoff_lat, lng: selected.dropoff_lng } : null}
            route={selected ? [[selected.pickup_lat, selected.pickup_lng], [selected.dropoff_lat, selected.dropoff_lng]] : null}
            center={[24.4539, 54.3773]}
            zoom={9}
          />
        </div>
      </div>

      {/* Drivers */}
      <div className="col-span-12 lg:col-span-3 bg-white rounded-2xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="font-display font-bold text-lg">Online drivers</p>
          <p className="text-xs text-slate-500">{drivers.length} available now</p>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {drivers.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">No drivers online</p>
          ) : drivers.map((d) => (
            <button
              key={d.id}
              data-testid={`assign-driver-${d.id}`}
              disabled={!selected}
              onClick={() => assign(d.id)}
              className="w-full p-3 rounded-xl border border-slate-100 hover:border-primary hover:bg-primary-50/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-left flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-display font-bold">
                {d.name?.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 text-sm">{d.name}</p>
                <p className="text-[10px] text-slate-400 font-mono">{d.enec_id}</p>
              </div>
              <Send size={14} className="text-primary" />
            </button>
          ))}
        </div>
        {groups && (
          <div className="p-3 border-t border-slate-100 text-xs text-slate-600 bg-primary-50/40">
            <p className="font-bold text-slate-900 mb-1">{groups.total} groups proposed</p>
            {groups.proposed_groups.slice(0, 3).map((g, i) => (
              <p key={i} className="font-mono">Group {i + 1}: {g.length} bookings</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
